import {
  Button,
  Dialog,
  InputBase,
  FormControlLabel,
  Select,
  styled,
} from '@mui/material';

/**
 * Cards com raio de 10px e sombra sutil; elementos internos (blocos, opções)
 * com 8px; botões e miniaturas com 6px, como PrimaryButton em shared/ui/Inputs.js.
 */
const GREEN = '#318300';
const GREEN_LIGHT = '#f0f7eb';
const BORDER = '#e8eae8';
const DIVIDER = '#eff1ef';
const SURFACE = '#f6f7f6';
const TITLE = '#1f2a24';
const TEXT = '#3f4a44';
const MUTED = '#79837d';
const ERROR = '#b3261e';

const CARD_RADIUS = '10px';
const RADIUS = '8px';
const SMALL_RADIUS = '6px';
const SHADOW =
  '0 1px 2px rgba(23, 43, 31, 0.04), 0 2px 8px rgba(23, 43, 31, 0.05)';

const MOBILE = '@media (max-width: 1124px)';
const DESKTOP = '@media (min-width: 1125px)';

/** Abaixo disso a página rola, em vez de espremer as colunas. */
const PANEL_MIN_HEIGHT = '420px';

/** Tokens compartilhados com a tela de configuração (config/, só local). */
export const tokens = {
  GREEN,
  GREEN_LIGHT,
  BORDER,
  DIVIDER,
  SURFACE,
  TITLE,
  TEXT,
  MUTED,
  ERROR,
  CARD_RADIUS,
  RADIUS,
  SMALL_RADIUS,
  SHADOW,
  MOBILE,
  DESKTOP,
  PANEL_MIN_HEIGHT,
};

// ─── Layout ───────────────────────────────────────────────────────────────────

/**
 * No desktop o conteúdo do WideLayout tem altura definida, então a página
 * ocupa 100% dela em flex: as colunas vão até o fim com o mesmo respiro
 * embaixo que em cima.
 */
export const PageShell = styled('div')({
  padding: '10px',
  boxSizing: 'border-box',
  width: '100%',
  color: TEXT,
  fontSize: '14px',
  [DESKTOP]: { display: 'flex', flexDirection: 'column', height: '100%' },
});

/** Título e contexto à esquerda, ação de reiniciar à direita. */
export const PageHeader = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '14px',
});

/**
 * Mesmo título da implantação (<h3 margin 10px 0>, 1.17em sobre os 16px do
 * body), para os cards começarem na mesma altura. O contexto vai na mesma
 * linha, em <small>, sem acrescentar altura.
 */
export const PageTitle = styled('h3')({
  flex: '0 0 auto',
  margin: '10px 0',
  fontSize: '1.17rem',
  fontWeight: 700,
  color: TITLE,
  '& small': {
    marginLeft: '12px',
    fontSize: '13.5px',
    fontWeight: 400,
    color: MUTED,
  },
});

/** Seletor de mock + Reiniciar, alinhados à direita do cabeçalho. */
export const HeaderActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  gap: '10px',
});

/** Título à esquerda e, à direita, o campo de teste do produto. */
export const TitleRow = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  flexWrap: 'wrap',
});

/** Campo para testar outro fluxo do banco pelo ID do produto. */
export const ProductSearchForm = styled('form')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ProductIdInput = styled(InputBase)({
  width: '170px',
  padding: '3px 10px',
  fontSize: '13.5px',
  borderRadius: SMALL_RADIUS,
  border: `1px solid ${BORDER}`,
  background: '#fff',
  '&.Mui-focused': { borderColor: GREEN },
});

/**
 * Select compacto no mesmo padrão dos botões: seletor de produto na tela de
 * pedido e regras de condição na tela de configuração.
 */
export const CompactSelect = styled(Select)({
  minWidth: '160px',
  fontSize: '13px',
  borderRadius: SMALL_RADIUS,
  background: '#fff',
  '& .MuiSelect-select': { padding: '7px 32px 7px 12px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cdd5cf' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: GREEN,
    borderWidth: '1px',
  },
});

export const ContextLine = styled('p')({
  margin: 0,
  fontSize: '13.5px',
  color: MUTED,
});

/** Topo do card de perguntas: abas à esquerda, "Reiniciar" à direita. */
export const CardTabsBar = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '0 16px',
  borderBottom: `1px solid ${DIVIDER}`,
});

/** "Terapia Principal - Acessórios" | "Terapias complementares". */
export const SectionTabs = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '22px',
});

export const SectionTab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  padding: '14px 2px 12px',
  border: 'none',
  borderBottom: `3px solid ${active ? GREEN : 'transparent'}`,
  marginBottom: '-1px',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: active ? 700 : 500,
  color: active ? GREEN : MUTED,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': { color: GREEN },
}));

/** Questionário (40%) | descrição (60%), com os 10px de gap da implantação. */
export const ContentGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: '10px',
  alignItems: 'stretch',
  [DESKTOP]: {
    flex: '1 1 auto',
    minHeight: PANEL_MIN_HEIGHT,
    gridTemplateColumns: 'minmax(0, 40fr) minmax(0, 60fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
  },
});

export const QuestionColumn = styled('div')({
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  [MOBILE]: { order: 2 },
});

export const ProductColumn = styled('div')({
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  [MOBILE]: { order: 1 },
});

export const Card = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  width: '100%',
  background: '#fff',
  border: `1px solid ${BORDER}`,
  borderRadius: CARD_RADIUS,
  boxShadow: SHADOW,
  boxSizing: 'border-box',
  overflow: 'hidden',
});

// ─── Coluna esquerda: questionário ────────────────────────────────────────────

export const StepHeader = styled('div')({
  flex: '0 0 auto',
  padding: '13px 15px 11px',
  borderBottom: `1px solid ${DIVIDER}`,
});

export const StepLabel = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: '10px',
  marginBottom: '8px',
  fontSize: '13.5px',
  '& strong': { color: GREEN, fontWeight: 600 },
  '& span': { color: MUTED, fontSize: '12px' },
});

export const ProgressTrack = styled('div')({
  width: '100%',
  height: '4px',
  background: '#e8e8e8',
  borderRadius: RADIUS,
  overflow: 'hidden',
});

export const ProgressFill = styled('div', {
  shouldForwardProp: (prop) => prop !== 'percent',
})<{ percent: number }>(({ percent }) => ({
  width: `${percent}%`,
  height: '100%',
  background: GREEN,
  transition: 'width 0.25s ease',
}));

/** Lista de blocos; rola quando o fluxo não cabe na altura da coluna. */
export const StepBody = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  padding: '14px 14px 6px',
});

/** Card de pergunta, sempre aberto. Fica vermelho quando o Prosseguir aponta para ele. */
export const QuestionCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'invalid' && prop !== 'grouped',
})<{ invalid?: boolean; grouped?: boolean }>(({ invalid, grouped }) => ({
  // Dentro de um bloco o espaçamento vem da árvore (TreeNode).
  marginBottom: grouped ? 0 : '12px',
  padding: '14px 16px 16px',
  borderRadius: RADIUS,
  border: `1.5px solid ${invalid ? '#e7b4ae' : BORDER}`,
  background: '#fff',
  scrollMarginTop: '12px',
}));

/** Bloco de perguntas ligadas: um só cartão que cresce ao responder. */
export const GroupCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'done',
})<{ done?: boolean }>(({ done }) => ({
  marginBottom: '12px',
  padding: '12px',
  borderRadius: RADIUS,
  border: `1.6px solid ${done ? '#b7d9a3' : '#cfd5d1'}`,
  background: '#fff',
  overflow: 'hidden',
}));

const TREE_LINE = '#d5dad6';
/** Altura do cotovelo: respiro entre cards (10px) + metade do cabeçalho do card. */
const ELBOW_HEIGHT = '34px';

/** Filhas de uma pergunta, recuadas. Sem recuo (`indent` falso), a hierarquia para de crescer. */
export const TreeChildren = styled('div', {
  shouldForwardProp: (prop) => prop !== 'indent',
})<{ indent: boolean }>(({ indent }) => ({
  '--indent': indent ? '20px' : '0px',
  marginLeft: 'var(--indent)',
  [MOBILE]: { '--indent': indent ? '14px' : '0px' },
}));

/**
 * Um card de pergunta com as suas filhas. `nested` abre o respiro acima e, com
 * `lined`, desenha a linha cinza: um cotovelo até o card e, se houver irmã
 * abaixo, o traço que continua descendo.
 */
export const TreeNode = styled('div', {
  shouldForwardProp: (prop) => prop !== 'nested' && prop !== 'lined',
})<{ nested?: boolean; lined?: boolean }>(({ nested, lined }) => ({
  position: 'relative',
  ...(nested && { paddingTop: '10px' }),
  ...(lined && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 'calc(var(--indent) / -2)',
      width: 'calc(var(--indent) / 2)',
      height: ELBOW_HEIGHT,
      borderLeft: `2px solid ${TREE_LINE}`,
      borderBottom: `2px solid ${TREE_LINE}`,
      borderBottomLeftRadius: '8px',
    },
    '&:not(:last-of-type)::after': {
      content: '""',
      position: 'absolute',
      top: ELBOW_HEIGHT,
      bottom: 0,
      left: 'calc(var(--indent) / -2)',
      borderLeft: `2px solid ${TREE_LINE}`,
    },
  }),
}));

/** Número do bloco; o mesmo número marca os produtos que ele adicionou. */
export const BlockNumber = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  background: '#f3f4f3',
  border: '1px solid #dddfdd',
  color: '#5d6560',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1,
});

/** Check de bloco concluído, à direita do título da pergunta raiz. */
export const DoneCheck = styled('span')({
  display: 'flex',
  color: GREEN,
  '& svg': { fontSize: '22px' },
});

export const BlockHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '12px',
});

export const BlockLead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: 0,
});

export const BlockTail = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flex: '0 0 auto',
});

export const QuestionTitle = styled('h2')({
  margin: 0,
  fontSize: '15.5px',
  fontWeight: 700,
  lineHeight: 1.4,
  letterSpacing: '-0.1px',
  color: TITLE,
});

/** "Opcional", à direita do título. */
export const BlockMeta = styled('span')({
  flex: '0 0 auto',
  fontSize: '12px',
  fontWeight: 500,
  color: MUTED,
  whiteSpace: 'nowrap',
});

/**
 * Alternativas (60%) e imagem do produto escolhido (40%). Perguntas de
 * confirmação não têm imagem, mas mantêm a mesma largura de alternativas.
 */
export const OptionsRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
  gap: '16px',
  alignItems: 'start',
  '& .MuiFormControlLabel-root:last-of-type': { marginBottom: 0 },
  [MOBILE]: { gridTemplateColumns: 'minmax(0, 1fr)' },
});

/** Foto do produto da alternativa escolhida, ou o ícone neutro. */
export const OptionImage = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '130px',
  borderRadius: RADIUS,
  background: SURFACE,
  color: '#b3bbb5',
  overflow: 'hidden',
  '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  '& svg': { fontSize: '44px' },
  [MOBILE]: { display: 'none' },
});

/**
 * Card de alternativa. O controle é o Radio/Checkbox nativo do MUI, que
 * desenha SVG sobre um input com opacity 0 — por isso não sofre com o
 * `input[type='radio']::after` global do portal (src/index.css).
 */
export const OptionControl = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  width: '100%',
  margin: '0 0 8px',
  padding: '11px 13px',
  borderRadius: RADIUS,
  border: `1px solid ${selected ? GREEN : BORDER}`,
  background: selected ? GREEN_LIGHT : '#fff',
  boxSizing: 'border-box',
  '&:hover': { borderColor: GREEN },
  '& .MuiButtonBase-root': {
    padding: 0,
    marginTop: '1px',
    color: '#c4cbc6',
    '&.Mui-checked': { color: GREEN },
  },
  '& .MuiFormControlLabel-label': { flex: '1 1 auto', minWidth: 0 },
  '& strong': {
    display: 'block',
    fontSize: '13.5px',
    fontWeight: 600,
    color: selected ? GREEN : TITLE,
    lineHeight: 1.4,
  },
  '& small': {
    display: 'block',
    marginTop: '3px',
    fontSize: '12.5px',
    color: MUTED,
    lineHeight: 1.5,
  },
}));

export const TextAction = styled(Button)({
  padding: 0,
  minWidth: 0,
  fontSize: '12.5px',
  fontWeight: 600,
  textTransform: 'none',
  color: GREEN,
  '&:hover': { background: 'transparent', textDecoration: 'underline' },
  '&.Mui-disabled': { color: '#b0b0b0' },
});

export const ErrorText = styled('p')({
  margin: '10px 0 0',
  fontSize: '12.5px',
  color: ERROR,
});

export const NavFooter = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  padding: '11px 15px',
  borderTop: `1px solid ${DIVIDER}`,
});

export const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'primary',
})<{ primary?: boolean }>(({ primary }) => ({
  minWidth: 0,
  padding: '8px 18px',
  fontSize: '13.5px',
  fontWeight: 600,
  borderRadius: SMALL_RADIUS,
  textTransform: 'none',
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  border: `1px solid ${primary ? GREEN : BORDER}`,
  background: primary ? GREEN : '#fff',
  color: primary ? '#fff' : TEXT,
  '&:hover': {
    boxShadow: 'none',
    background: primary ? '#2a580e' : SURFACE,
    border: `1px solid ${primary ? '#2a580e' : BORDER}`,
  },
  '&.Mui-disabled': {
    background: SURFACE,
    color: '#aeb6b0',
    border: `1px solid ${BORDER}`,
  },
}));

// ─── Coluna central: produto ──────────────────────────────────────────────────

export const TabBar = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  padding: '0 10px',
  borderBottom: `1px solid ${DIVIDER}`,
});

export const TabButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  padding: '14px 14px 13px',
  border: 'none',
  borderBottom: `2px solid ${active ? GREEN : 'transparent'}`,
  marginBottom: '-1px',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: active ? 700 : 500,
  color: active ? GREEN : MUTED,
  cursor: 'pointer',
  '&:hover': { color: GREEN },
}));

export const ProductBody = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  padding: '18px 20px 20px',
});

export const ProductHead = styled('div')({
  display: 'flex',
  gap: '22px',
  [MOBILE]: { flexDirection: 'column', gap: '14px' },
});

export const ImageBox = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '260px',
  height: '290px',
  borderRadius: RADIUS,
  background: SURFACE,
  color: '#a7b0aa',
  overflow: 'hidden',
  '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  '& svg': { fontSize: '62px' },
  [MOBILE]: { width: '100%', height: '200px' },
});

export const ProductInfo = styled('div')({
  flex: '1 1 auto',
  minWidth: 0,
  paddingTop: '2px',
});

export const ProductName = styled('h2')({
  margin: 0,
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: '-0.2px',
  color: TITLE,
});

export const ProductText = styled('p')({
  margin: '8px 0 0',
  fontSize: '13.5px',
  lineHeight: 1.65,
  color: MUTED,
});

export const SectionDivider = styled('hr')({
  height: 0,
  margin: '18px 0 14px',
  border: 'none',
  borderTop: `1px solid ${DIVIDER}`,
});

export const EmptyState = styled('p')({
  flex: '1 1 auto',
  margin: 0,
  padding: '40px 16px',
  fontSize: '13px',
  color: '#999',
  textAlign: 'center',
});

/** Ocupa a altura restante da coluna; o texto longo rola dentro dele. */
export const ProductCard = styled(Card)({
  flex: '1 1 auto',
  minHeight: 0,
});

// ─── Arquivos ─────────────────────────────────────────────────────────────────

export const FileGrid = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
});

/** Um PDF do produto: ícone + nome, clique para baixar. */
export const FileTile = styled('button')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  width: '128px',
  padding: '14px 10px',
  borderRadius: RADIUS,
  border: `1px solid ${BORDER}`,
  background: '#fff',
  fontFamily: 'inherit',
  fontSize: '12px',
  lineHeight: 1.4,
  color: TEXT,
  textAlign: 'center',
  wordBreak: 'break-word',
  cursor: 'pointer',
  '& svg': { fontSize: '34px', color: '#c62828' },
  '&:hover': { borderColor: GREEN },
});

// ─── Pop-up "Especificar modelo" ──────────────────────────────────────────────

export const ModelDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    borderRadius: CARD_RADIUS,
    boxShadow: SHADOW,
    maxWidth: '520px',
    width: '100%',
  },
  '& .MuiDialogTitle-root': {
    fontSize: '16px',
    fontWeight: 700,
    color: TITLE,
  },
});

export const ModelRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '11px 0',
  borderBottom: `1px solid ${DIVIDER}`,
  '&:last-of-type': { borderBottom: 'none' },
  /* O texto é o 2º div (o 1º é a foto) — flex só nele, senão a foto estica. */
  '& > div:nth-of-type(2)': { flex: 1, minWidth: 0 },
  /* padding: 0 anula o `h4 { padding-left: 5px }` global do portal (index.css). */
  '& h4': {
    margin: 0,
    padding: 0,
    fontSize: '13.5px',
    fontWeight: 600,
    lineHeight: 1.4,
    color: TEXT,
  },
});

/** Nome do produto e, embaixo, a tag com o código (sempre em coluna). */
export const ModelName = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '6px',
});

/** Tag neutra com o código SAP (só o número). */
export const SapTag = styled('span')({
  flex: '0 0 auto',
  padding: '2px 11px',
  borderRadius: '99px',
  border: '1px solid #dddfdd',
  background: '#f3f4f3',
  fontSize: '12.5px',
  fontWeight: 600,
  lineHeight: 1.5,
  color: '#5d6560',
});

/** Campo de busca no topo do pop-up. */
export const PickerSearch = styled(InputBase)({
  width: '100%',
  padding: '5px 12px',
  fontSize: '13.5px',
  borderRadius: SMALL_RADIUS,
  border: `1px solid ${BORDER}`,
  background: '#fff',
  '& svg': { fontSize: '18px', color: MUTED, marginRight: '8px' },
  '&.Mui-focused': { borderColor: GREEN },
});

/** Foto do modelo, na linha do pop-up. */
export const ModelThumb = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  width: '72px',
  height: '72px',
  borderRadius: SMALL_RADIUS,
  background: SURFACE,
  color: '#a7b0aa',
  overflow: 'hidden',
  '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  '& svg': { fontSize: '28px' },
});

// ─── Produtos adicionais (carrossel na coluna da direita) ─────────────────────

export const ExtrasTitle = styled('h3')({
  margin: 0,
  fontSize: '16px',
  fontWeight: 700,
  letterSpacing: '-0.1px',
  color: TITLE,
});

/**
 * Corpo sem rolagem vertical: a descrição ocupa o que sobra e rola só ela, se
 * o texto for longo; "Produtos adicionais" tem altura fixa e compacta.
 */
export const FitBody = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 20px 14px',
  overflow: 'hidden',
});

export const FitHead = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
});

export const ExtrasBlock = styled('div')({
  flex: '0 0 auto',
});

export const ExtrasEmpty = styled('p')({
  margin: 0,
  padding: '10px 0 4px',
  fontSize: '13px',
  color: MUTED,
});

export const ExtrasHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '8px',
});

export const RailButtons = styled('div')({
  display: 'flex',
  gap: '8px',
  '& .MuiIconButton-root': {
    width: '28px',
    height: '28px',
    border: `1px solid ${BORDER}`,
    color: TEXT,
    background: '#fff',
  },
  '& .MuiIconButton-root:hover': { borderColor: GREEN, color: GREEN },
});

export const ExtrasRail = styled('div')({
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  paddingBottom: '6px',
  scrollSnapType: 'x proximity',
});

/** Card do carrossel: foto em cima, nome embaixo. */
export const ExtraCard = styled('div')({
  position: 'relative',
  flex: '0 0 auto',
  width: '150px',
  borderRadius: RADIUS,
  border: `1px solid ${BORDER}`,
  background: '#fff',
  overflow: 'hidden',
  scrollSnapAlign: 'start',
  '& > div:first-of-type': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '84px',
    background: SURFACE,
    color: '#b3bbb5',
  },
  '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  '& svg': { fontSize: '30px' },
  '& strong': {
    display: 'block',
    padding: '8px 8px',
    fontSize: '12.5px',
    fontWeight: 600,
    lineHeight: 1.35,
    textAlign: 'center',
    color: TITLE,
  },
});

/** Select de escolha do produto do balaio, sob as alternativas. */
export const ChoiceField = styled('div')({
  marginTop: '10px',
  '& .MuiSelect-select': { whiteSpace: 'normal' },
});
